"use client";

import { type FC, type HTMLAttributes, type Ref, memo } from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses, limit } from "@huub-web/utils";

import type { ClassNameProp } from "../../props";
import { Text } from "../Text";

import {
  gaugeContainerStyle,
  gaugeTrackStyle,
  gaugeFillStyle,
  gaugePointerStyle,
  trackStrokeStyle,
  valueStyle,
} from "./Gauge.css";

export type GaugeProps = HTMLAttributes<HTMLDivElement> &
  ClassNameProp &
  RecipeVariants<typeof gaugeContainerStyle> &
  RecipeVariants<typeof trackStrokeStyle> & {
    /**
     * Forwarded ref for the root div element
     */
    ref?: Ref<HTMLDivElement>;
    /**
     * The progress value (0-100) or range [start, end]
     */
    value?: number | [number, number];
    /**
     * The pointer value (0-100)
     */
    pointerValue?: number;
    /**
     * The minimum value (default: 0)
     */
    min?: number;
    /**
     * The maximum value (default: 100)
     */
    max?: number;
    /**
     * Start angle in degrees (default: -90, which is 12 o'clock)
     */
    startAngle?: number;
    /**
     * End angle in degrees (default: 90, which creates a semicircle)
     */
    endAngle?: number;
    /**
     * Number of subdivision lines (default: 4)
     */
    subdivisions?: number;
    /**
     * Class name for the fill element
     */
    gaugeClassName?: string;
    /**
     * Class name for the track stroke element
     */
    trackClassName?: string;
    /**
     * Whether to show the needle
     */
    needle?: boolean;
    /**
     * Whether to show the value
     */
    showValue?: boolean;
  };

/**
 * A gauge component that displays progress as a circular arc.
 * Features transparent background and neutral colors.
 */
export const Gauge: FC<GaugeProps> = memo(
  ({
    ref,
    size = "m",
    variant,
    value = 0,
    pointerValue,
    min = 0,
    max = 100,
    startAngle = -180,
    endAngle = 0,
    subdivisions = 0,
    alignSelf,
    trackClassName,
    className,
    needle = true,
    showValue = false,
    ...props
  }) => {
    // Handle both single values and ranges
    const isRange = Array.isArray(value);
    const startValue = isRange ? limit(value[0], min, max) : min;
    const endValue = isRange
      ? limit(value[1], min, max)
      : limit(value || 0, min, max);

    const startPercentage = ((startValue - min) / (max - min)) * 100;
    const endPercentage = ((endValue - min) / (max - min)) * 100;

    // Calculate the angle range
    const angleRange = endAngle - startAngle;
    const startFillAngle = startAngle + (angleRange * startPercentage) / 100;
    const endFillAngle = startAngle + (angleRange * endPercentage) / 100;

    // Convert angles to radians for calculations
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const startFillRad = (startFillAngle * Math.PI) / 180;
    const endFillRad = (endFillAngle * Math.PI) / 180;

    // SVG dimensions based on size
    const dimensions = {
      s: { size: 80, strokeWidth: 4 },
      m: { size: 120, strokeWidth: 6 },
      l: { size: 160, strokeWidth: 8 },
      displayM: { size: 80, strokeWidth: 4 },
    };

    const { size: svgSize, strokeWidth } = dimensions[size || "m"];
    const radius = (svgSize - strokeWidth) / 2;
    const center = svgSize / 2;

    // Calculate path for the track (full arc)
    const trackPath = createArcPath(center, center, radius, startRad, endRad);

    // Calculate path for the fill (progress arc)
    const fillPath =
      endPercentage > startPercentage
        ? createArcPath(center, center, radius, startFillRad, endFillRad)
        : "";

    // Calculate pointer position (uses pointerValue if supplied, otherwise points to the highest value)
    const pointerLength = radius - strokeWidth / 2;
    const pointerSize = strokeWidth / 2;

    const limitedPointerValue = limit(pointerValue ?? endValue, min, max);
    const pointerPercentage = ((limitedPointerValue - min) / (max - min)) * 100;
    const pointerAngle = startAngle + (angleRange * pointerPercentage) / 100;
    const pointerRad = (pointerAngle * Math.PI) / 180;

    // Round pointer coordinates to prevent SSR hydration mismatches
    const pointerX =
      Math.round((center + pointerLength * Math.cos(pointerRad)) * 1000000) /
      1000000;
    const pointerY =
      Math.round((center + pointerLength * Math.sin(pointerRad)) * 1000000) /
      1000000;

    const pointerPath = createPointerPath(
      center,
      center,
      pointerX,
      pointerY,
      pointerSize,
    );

    // Calculate track segments (separated by transparent gaps)
    const trackSegments = [];
    if (subdivisions > 1) {
      for (let i = 0; i < subdivisions; i++) {
        const segmentStartAngle =
          startRad + ((endRad - startRad) * i) / subdivisions;
        const segmentEndAngle =
          startRad + ((endRad - startRad) * (i + 1)) / subdivisions;

        // Add small gap between segments
        const gapSize = 0.05; // Small gap in radians
        const adjustedStartAngle =
          i === 0 ? segmentStartAngle : segmentStartAngle + gapSize;
        const adjustedEndAngle =
          i === subdivisions - 1 ? segmentEndAngle : segmentEndAngle - gapSize;

        if (adjustedEndAngle > adjustedStartAngle) {
          trackSegments.push(
            createArcPath(
              center,
              center,
              radius,
              adjustedStartAngle,
              adjustedEndAngle,
            ),
          );
        }
      }
    } else {
      // No subdivisions, use full track
      trackSegments.push(trackPath);
    }

    return (
      <div
        ref={ref}
        className={mergeClasses(
          gaugeContainerStyle({ size, alignSelf }),
          className,
        )}
        {...props}
      >
        <svg
          width={svgSize}
          height={svgSize / 2 + 10}
          viewBox={`0 0 ${svgSize} ${svgSize / 2 + 10}`}
          className={gaugeTrackStyle}
          aria-hidden="true"
        >
          {/* Track segments (with transparent gaps) */}
          {trackSegments.map((segment, index) => (
            <path
              key={index}
              d={segment}
              fill="none"
              strokeWidth={strokeWidth}
              strokeLinecap={subdivisions > 1 ? "butt" : "round"}
              className={mergeClasses(
                trackStrokeStyle({ variant }),
                trackClassName,
              )}
            />
          ))}

          {/* Fill (progress arc) */}
          {fillPath && (
            <path
              d={fillPath}
              fill="none"
              stroke="url(#gaugeLinearGradient)"
              strokeWidth={strokeWidth}
              strokeLinecap={subdivisions > 1 ? "butt" : "round"}
              className={mergeClasses(gaugeFillStyle)}
            />
          )}

          {/* Pointer/Needle - points to highest value */}
          {needle && (
            <path d={pointerPath} fill="#000" className={gaugePointerStyle} />
          )}

          {/* Center dot */}
          {needle && (
            <circle cx={center} cy={center} r={strokeWidth / 3} fill="#000" />
          )}

          <defs>
            <linearGradient
              id="gaugeLinearGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#C467D5" />
              <stop offset="50%" stopColor="#FF6B6E" />
              <stop offset="80%" stopColor="#FB9458" />
            </linearGradient>
          </defs>
        </svg>

        {showValue && (
          <Text bold className={valueStyle}>
            {value}%
          </Text>
        )}
      </div>
    );
  },
);

/**
 * Creates an SVG arc path
 */
function createArcPath(
  centerX: number,
  centerY: number,
  radius: number,
  startAngle: number,
  endAngle: number,
): string {
  // Round to 6 decimal places to prevent SSR hydration mismatches
  const startX =
    Math.round((centerX + radius * Math.cos(startAngle)) * 1000000) / 1000000;
  const startY =
    Math.round((centerY + radius * Math.sin(startAngle)) * 1000000) / 1000000;
  const endX =
    Math.round((centerX + radius * Math.cos(endAngle)) * 1000000) / 1000000;
  const endY =
    Math.round((centerY + radius * Math.sin(endAngle)) * 1000000) / 1000000;

  const largeArcFlag = endAngle - startAngle <= Math.PI ? "0" : "1";

  return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
}

/**
 * Creates a pointer/needle path pointing from center to target position with rounded bottom
 */
function createPointerPath(
  centerX: number,
  centerY: number,
  targetX: number,
  targetY: number,
  width: number,
): string {
  // Calculate the angle of the pointer
  const angle = Math.atan2(targetY - centerY, targetX - centerX);

  // Calculate perpendicular angle for the base width
  const perpAngle = angle + Math.PI / 2;

  // Create pointer with rounded bottom
  const baseWidth = width * 0.65;
  const radius = baseWidth; // Radius for the rounded bottom

  // Side points where the straight edges meet the arc - round to prevent SSR mismatches
  const side1X =
    Math.round((centerX + baseWidth * Math.cos(perpAngle)) * 1000000) / 1000000;
  const side1Y =
    Math.round((centerY + baseWidth * Math.sin(perpAngle)) * 1000000) / 1000000;
  const side2X =
    Math.round((centerX - baseWidth * Math.cos(perpAngle)) * 1000000) / 1000000;
  const side2Y =
    Math.round((centerY - baseWidth * Math.sin(perpAngle)) * 1000000) / 1000000;

  // Round target coordinates as well
  const roundedTargetX = Math.round(targetX * 1000000) / 1000000;
  const roundedTargetY = Math.round(targetY * 1000000) / 1000000;
  const roundedRadius = Math.round(radius * 1000000) / 1000000;

  // Create path with rounded bottom using arc
  return `M ${side1X} ${side1Y} L ${roundedTargetX} ${roundedTargetY} L ${side2X} ${side2Y} A ${roundedRadius} ${roundedRadius} 0 0 1 ${side1X} ${side1Y} Z`;
}
